import { forwardRef, useRef, useState, useEffect, MouseEvent, ChangeEvent, ReactNode } from 'react';
import { Container, Content, Feedback, FloatingLabel, StaticLabel, WidthMaster } from './index.styles';
import { Button } from '../Button';
import { Icon } from '../../atoms/Icon';
import { IconNames } from '../../../theme';
import { Popover } from '../Popover';
import { AdvancedSelect, AdvancedSelectProps } from '../AdvancedSelect';

export const autoCompletes = ['on', 'off'] as const;
export const masks = ['onlyLetters', 'onlyNumbers', 'noSymbols'] as const;
export const sizes = ['sm', 'md', 'lg', 'xl'] as const;
export const types = ['text', 'email', 'password', 'date'] as const;

type PresetMask = (typeof masks)[number];
export type InputMask = PresetMask | string | string[];

const customMaskTokens = ['#', 'n', 'N', 'l', 'L'];
const isAnyCharacter = (value: string) => value.length > 0;
const isLetter = (value: string) => /^\p{L}$/u.test(value);
const isNumber = (value: string) => /^\d$/.test(value);
const customTokenMatchers: Record<string, (value: string) => boolean> = {
  '#': isAnyCharacter,
  n: isNumber,
  N: isNumber,
  l: isLetter,
  L: isLetter,
};

const applyPatternMask = (value: string, pattern: string, tokenMatchers: Record<string, (value: string) => boolean>) => {
  const chars = Array.from(value);
  let charIndex = 0;
  let pendingLiterals = '';
  let maskedValue = '';

  for (const patternChar of pattern) {
    const matcher = tokenMatchers[patternChar];

    if (!matcher) {
      pendingLiterals += patternChar;

      if (chars[charIndex] === patternChar) {
        charIndex += 1;
      }

      continue;
    }

    let nextValue = '';

    while (charIndex < chars.length) {
      const char = chars[charIndex];

      charIndex += 1;

      if (matcher(char)) {
        nextValue = char;
        break;
      }
    }

    if (!nextValue) break;

    maskedValue += `${pendingLiterals}${nextValue}`;
    pendingLiterals = '';
  }

  return maskedValue;
};

const getMaskCapacity = (pattern: string) => pattern.split('').filter((char) => customMaskTokens.includes(char)).length;

const getMaskValueLength = (value: string, pattern: string) => {
  const chars = Array.from(value);
  let charIndex = 0;
  let valueLength = 0;

  for (const patternChar of pattern) {
    const matcher = customTokenMatchers[patternChar];

    if (charIndex >= chars.length) break;

    if (!matcher) {
      if (chars[charIndex] === patternChar) {
        charIndex += 1;
      }

      continue;
    }

    while (charIndex < chars.length) {
      const char = chars[charIndex];

      charIndex += 1;

      if (matcher(char)) {
        valueLength += 1;
        break;
      }
    }
  }

  return valueLength + chars.length - charIndex;
};

const getCustomMask = (value: string, mask: string | string[]) => {
  if (Array.isArray(mask)) {
    return mask.find((pattern) => getMaskValueLength(value, pattern) <= getMaskCapacity(pattern)) || mask[mask.length - 1];
  }

  return mask;
};

const applyInputMask = (value: string, mask?: InputMask) => {
  if (!mask) return value;

  if (mask === 'onlyLetters') return value.replace(/[^\p{L}]/gu, '');
  if (mask === 'onlyNumbers') return value.replace(/\D/g, '');
  if (mask === 'noSymbols') return value.replace(/[^\p{L}\d]/gu, '');

  return applyPatternMask(value, getCustomMask(value, mask), customTokenMatchers);
};

export interface InputFeedbackType {
  /**
   * Feedback message. Optional.
   */
  message?: string;
  /**
   * Sets a time in milliseconds to display feedbacks. Optional.
   */
  feedbackDuration?: number;
  /**
   * Emits an event to the parent when the feedbackDuration reaches the set time. Optional.
   */
  onFeedbackDurationEnd?: () => void;
  /**
   * Feedback type. This prop handles the color urgency of the feedback. Optional.
   */
  type?: 'success' | 'danger' | 'warning' | 'info';
}

interface ButtonProp {
  /**
   * Button click event emitter. Optional.
   */
  click?: (e: MouseEvent) => void;
  /**
   * Button icon. Optional.
   */
  icon?: IconNames;
  /**
   * Button label. Optional.
   */
  label?: string;
  /**
   * Button is disabled. Optional.
   */
  disabled?: boolean;
}

interface PopOverProp {
  /**
   * Popup information content. JSX content type. Optional.
   */
  content?: ReactNode;
  /**
   * Use a button to trigger the popOver and places an icon on it. If this and label are undefined, the popOver will trigger upon clicking in the Input. Optional.
   */
  icon?: IconNames;
  /**
   * Use a button to trigger the popOver and places a label on it. If this and label are undefined, the popOver will trigger upon clicking in the Input. Optional.
   */
  label?: string;
}

interface SelectProp {
  /**
   * Select icon name. Optional.
   */
  icon?: AdvancedSelectProps['icon'];
  /**
   * Select id. Required.
   */
  id: AdvancedSelectProps['id'];
  /**
   * Select label. Required.
   */
  label: AdvancedSelectProps['label'];
  /**
   * Select event emitter upon option selection. Required.
   */
  onSelect: AdvancedSelectProps['onSelect'];
  /**
   * Select option list. Required.
   */
  options: AdvancedSelectProps['options'];
  /**
   * Current selected option. In this case it's an array with only one option. E.g.: [{label: 'option', action: 'option1'}]. Options.
   */
  selectedOptions?: AdvancedSelectProps['selectedOptions'];
}

export interface InputProps extends Component {
  /**
   * Width of input automatically adjusts to text input. Optional.
   */
  adaptWidthToText?: boolean;
  /**
   * Aria Label. Optional.
   */
  ariaLabel?: string;
  /**
   * Has autocomplete? Optional.
   */
  autoComplete?: (typeof autoCompletes)[number];
  /**
   * Has autofocus? Optional.
   */
  autoFocus?: boolean;
  /**
   * Blur event emitter. Optional.
   */
  blur?: (e: string) => void;
  /**
   * Button. E.g.: {}: Optional.
   */
  button?: ButtonProp;
  /**
   * Change event emitter. Optional.
   */
  change?: (e: string) => void;
  /**
   * Set dark mode colors or not. Optional.
   */
  darkmode?: boolean;
  /**
   * Delay time in milliseconds to emmit change event. Optional.
   */
  debounceInterval?: number;
  /**
   * Visual feedback message and type ('success', 'warning', 'info' or 'danger'). (E.g: {type: 'danger', message: 'Password is wrong'}). Optional.
   */
  feedback?: InputFeedbackType;
  /**
   * Icon name. If it has icon, icon will be applied. Optional. (You can see the full list at branding/icons)
   */
  icon?: IconNames;
  /**
   * ID attribute. Optional.
   */
  id?: string;
  /**
   * Initial value. Optional.
   */
  initialValue?: string;
  /**
   * Disable state attribute. Optional.
   */
  isDisabled?: boolean;
  /**
   * Loading state. Optional.
   */
  isLoading?: boolean;
  /**
   * Readonly state attribute. Optional.
   */
  isReadonly?: boolean;
  /**
   * Required state attribute. Optional.
   */
  isRequired?: boolean;
  /**
   * On keyboard press event emitter. Optional.
   */
  keyPress?: (key: string, text: string) => void;
  /**
   * Input label text and type ('internal' or 'external'). Required.
   */
  label: string;
  /**
   * Masks the input value. Presets: onlyLetters, onlyNumbers, noSymbols.
   * Custom masks use # for any character, n/N for numbers and l/L for letters. Optional.
   */
  mask?: InputMask;
  /**
   * Max character lenght. Optional.
   */
  maxLength?: number;
  /**
   * Max width. Optional.
   */
  maxWidth?: string;
  /**
   * Min width. Optional.
   */
  minWidth?: string;
  /**
   * Input placeholder for useStaticLabel use case. Optional.
   */
  placeholder?: string;
  /**
   * Adds a popover to the input. Optional.
   */
  popOver?: PopOverProp;
  /**
   * Adds the select component to the input. Optional.
   */
  select?: SelectProp;
  /**
   * Sizing variations. Optional.
   */
  size?: (typeof sizes)[number];
  /**
   * Hover title. Optional.
   */
  title?: string;
  /**
   * Input type attribute.
   */
  type?: (typeof types)[number];
  /**
   * Always show the label above the input instead of using the default floating label. Optional.
   */
  useStaticLabel?: boolean;
  /**
   * value. Optional.
   */
  value?: string;
}

/**
 * Input
 */
export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    adaptWidthToText = false,
    ariaLabel = '',
    autoComplete = 'on',
    autoFocus,
    blur,
    button,
    change,
    className = '',
    customClass = '',
    darkmode,
    debounceInterval,
    feedback,
    icon,
    id,
    initialValue = '',
    isDisabled,
    isLoading,
    isReadonly,
    isRequired,
    keyPress,
    label,
    mask,
    maxLength,
    maxWidth,
    minWidth,
    placeholder,
    popOver,
    select,
    size = 'md',
    testId = 'input',
    title,
    type = 'text',
    useStaticLabel = false,
    value = '',
  } = props;

  const [hasFocus, setHasFocus] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isLabelFloating, setIsLabelFloating] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [inputValue, setInputValue] = useState(applyInputMask(value, mask));
  const [debounce, setDebounce] = useState<ReturnType<typeof setTimeout>>(setTimeout(() => null, 0));
  const [feedbackTimer, setFeedbackTimer] = useState<ReturnType<typeof setTimeout>>(setTimeout(() => null, 0));
  const inputRef = (ref || useRef<HTMLInputElement>(null)) as React.RefObject<HTMLInputElement>;
  const widthMasterRef = useRef<HTMLDivElement>(null);

  const setInputWidth = () => {
    setTimeout(() => {
      if (adaptWidthToText && widthMasterRef?.current && inputRef?.current) {
        const adjustOffset = 10;
        const widthMasterStyles = window.getComputedStyle(widthMasterRef.current);
        const widthAdjusted = parseInt(widthMasterStyles.width, 10) + adjustOffset;

        if (widthAdjusted) {
          inputRef.current.style.width = `${widthAdjusted}px`;
        }
      }
    }, 0);
  };

  const getIconSize = () => {
    if (size === 'sm') return 12;
    if (size === 'md') return 14;
    if (size === 'lg') return 16;

    return 18;
  };

  const getRightElement = () => {
    if (popOver?.label || popOver?.icon) {
      return (
        <Popover
          content={popOver?.content}
          useClick
          placement="right-start"
          maxWidth="17.5rem"
        >
          <Button
            data-testid="popover-button"
            appearance="default"
            iconPosition="right"
            icon={popOver.icon}
            label={popOver.label}
            size={getIconSize()}
            tabIndex={-1}
            variation="default"
          />
        </Popover>
      );
    }

    if (type === 'password') {
      return (
        <Button
          data-testid="button"
          appearance="default"
          onClick={() => setShowPassword(!showPassword)}
          icon={showPassword ? 'eye' : 'eye_off'}
          size={getIconSize()}
          variation="default"
        />
      );
    }
    if (feedback?.type) {
      const feedbackIcon = () => {
        if (feedback.type === 'danger') return 'danger';
        if (feedback.type === 'warning') return 'warning';
        if (feedback.type === 'success') return 'check';

        return 'info';
      };

      return (
        <Icon
          name={feedbackIcon()}
          customClass="right-icon"
        />
      );
    }
    if (button) {
      return (
        <Button
          data-testid="button"
          appearance="default"
          onClick={(e) => button.click && button.click(e)}
          icon={button.icon}
          iconPosition="right"
          label={button.label}
          size={getIconSize()}
          variation="default"
          disabled={button.disabled}
        />
      );
    }

    return null;
  };

  const handleBlur = (e: string) => {
    const valueToSet = initialValue;

    if (!valueToSet && !inputRef.current.value) setIsLabelFloating(false);
    if (popOver) setIsPopoverOpen(false);
    if (e.length > 0) setIsLabelFloating(true);
    else setIsLabelFloating(false);
    setHasFocus(false);
    if (blur) blur(e);
  };

  const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const text = applyInputMask(e.target.value, mask);

    if (text.length > 0) setIsLabelFloating(true);
    else setIsLabelFloating(false);
    if (hasFocus) setIsLabelFloating(true);

    if (text.trim() === '' && widthMasterRef?.current) {
      setInputValue('');
      widthMasterRef.current.innerHTML = '';
      setInputWidth();
    } else {
      setInputValue(text);
    }

    if (debounceInterval) {
      clearTimeout(debounce);

      const debounceCallback = setTimeout(() => {
        if (change) change(text);
      }, debounceInterval);

      setDebounce(debounceCallback);
    } else {
      if (change) change(text);
    }
  };

  const handleKeyPress = (key: string) => {
    clearTimeout(debounce);

    setInputWidth();

    if (keyPress) keyPress(key, inputValue);
  };

  const handleFocus = () => {
    if (popOver) setIsPopoverOpen(true);
    setHasFocus(true);
    setIsLabelFloating(true);
  };

  useEffect(() => {
    const valueToSet = applyInputMask(value || initialValue, mask);

    setInputValue(valueToSet);
    if (valueToSet && inputRef?.current) {
      inputRef.current.value = valueToSet;
    } else {
      setIsLabelFloating(false);
    }

    if (adaptWidthToText && widthMasterRef?.current) {
      if (valueToSet) {
        widthMasterRef.current.innerHTML = valueToSet;
      }
      widthMasterRef.current.style.width = 'auto';
      setInputWidth();
    }
  }, [initialValue, value, mask]);

  useEffect(() => {
    if (feedback?.feedbackDuration && feedback?.onFeedbackDurationEnd) {
      clearTimeout(feedbackTimer);

      setFeedbackTimer(setTimeout(() => feedback.onFeedbackDurationEnd?.(), feedback.feedbackDuration));
    }
  }, [keyPress, change]);

  return (
    <Container
      className={`input input-${size} ${customClass} ${className}`}
      isDisabled={isDisabled}
      isLoading={isLoading}
    >
      {useStaticLabel && <StaticLabel darkmode={!!darkmode}>{label}</StaticLabel>}
      <Content
        darkmode={darkmode}
        data-testid={testId}
        feedback={feedback}
        float={isLabelFloating}
        $hasSelect={!!select}
        htmlFor={id}
        icon={icon}
        isDisabled={isDisabled}
        isReadonly={isReadonly}
        maxWidth={maxWidth}
        minWidth={minWidth}
        onClick={(e) => select && e.preventDefault()}
        size={size}
        useStaticLabel={!!useStaticLabel}
      >
        {select && (
          <AdvancedSelect
            icon={select.icon}
            id={select.id}
            label={select.label}
            listOffset={0}
            onSelect={select.onSelect}
            options={select.options}
            placement="bottom-start"
            selectedOptions={select.selectedOptions}
            size={size}
          />
        )}
        {icon && (
          <Icon
            name={icon}
            customClass="left-icon"
          />
        )}
        <input
          aria-label={ariaLabel}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          data-testid={`${testId}-input`}
          disabled={isDisabled}
          id={id}
          name={id}
          maxLength={maxLength}
          onFocus={handleFocus}
          onChange={(e) => handleChange(e)}
          onBlur={(e) => handleBlur(e.target.value)}
          onKeyDown={(e) => handleKeyPress(e.key)}
          placeholder={useStaticLabel && placeholder ? placeholder : label}
          readOnly={isReadonly}
          ref={inputRef}
          required={isRequired}
          title={title}
          type={showPassword ? 'text' : type}
          value={inputValue}
        />

        {adaptWidthToText && widthMasterRef && (
          <WidthMaster
            ref={widthMasterRef}
            data-testid={`${testId}-width-master`}
          >
            {inputValue}
          </WidthMaster>
        )}

        {size !== 'sm' && size !== 'md' && !useStaticLabel && !select && (
          <FloatingLabel
            className="input-label"
            darkmode={darkmode}
            float={isLabelFloating}
            icon={icon}
          >
            {label}
          </FloatingLabel>
        )}
        {getRightElement()}
      </Content>
      {popOver && !popOver.label && (
        <Popover
          content={popOver?.content}
          customClass={useStaticLabel ? 'full-popover-with-label' : 'full-popover'}
          isVisibleOutside={isPopoverOpen}
          placement="right-start"
          maxWidth="19rem"
          useOutsideVisibility
        />
      )}
      {feedback && (
        <Feedback
          className="input-feedback"
          darkmode={darkmode}
          feedback={feedback}
        >
          {feedback.message}
        </Feedback>
      )}
    </Container>
  );
});
